import { useCallback, useEffect, useState } from "react";
import { GetSponsorsByUserAPI } from "@/services/api/sponsor/getSponsorsByUser";
import { getSponsorOwnerCompletionsAPI } from "@/services/api/sponsor/sponsorOwnerCompletionsApi";

const COMPLETION_PAGE_SIZE = 10;

export type SponsorTaskSummary = {
  id: string;
  title?: string | null;
  type?: string | null;
};

export type BusinessSponsor = {
  id: string;
  name?: string | null;
  description?: string | null;
  image_url?: string | null;
  ctime?: string | null;
  tasks?: SponsorTaskSummary[];
};

export type SponsorTaskCompletion = {
  id: string;
  task_id?: string | null;
  student_id?: string | null;
  completed?: boolean | null;
  proof?: any;
  queue_job_status?: string | null;
  queue_job_id?: string | null;
  review_count?: number;
  /** Set locally after a review so the buttons stay disabled, like the RN screen. */
  reviewedAt?: string | null;
  ctime?: string | null;
  utime?: string | null;
  task_title?: string | null;
  type?: string | null;
};

export type SponsorCompletionGroup = {
  sponsor_id: string;
  student_id: string;
  student_name?: string | null;
  sponsor?: { id: string; name?: string | null; image_url?: string | null };
  sponsor_activity?: {
    id: string;
    activity_name?: string | null;
    activity_date?: string | null;
    proof_code?: string | null;
    type?: string | null;
    official_ig_account?: string | null;
    committee_ig_accounts?: unknown;
  } | null;
  sponsor_task_completions?: SponsorTaskCompletion[];
};

/**
 * Business sponsor centre data: the sponsors created by the business (paginated)
 * and the task completions those sponsors' students submitted (read-only).
 */
export function useBusinessSponsors() {
  const [sponsors, setSponsors] = useState<BusinessSponsor[]>([]);
  const [sponsorTotal, setSponsorTotal] = useState(0);
  const [sponsorPage, setSponsorPage] = useState(1);
  const [sponsorsLoading, setSponsorsLoading] = useState(true);
  const [sponsorsRefreshing, setSponsorsRefreshing] = useState(false);

  const [groups, setGroups] = useState<SponsorCompletionGroup[]>([]);
  const [hasMoreCompletions, setHasMoreCompletions] = useState(false);
  const [completionPage, setCompletionPage] = useState(1);
  const [completionsLoading, setCompletionsLoading] = useState(true);
  const [completionsRefreshing, setCompletionsRefreshing] = useState(false);

  const fetchSponsors = useCallback(
    async (targetPage: number, refreshing = false) => {
      if (refreshing) setSponsorsRefreshing(true);
      const resp = await GetSponsorsByUserAPI({ page: targetPage });
      if (resp?.status === 0 && resp.result) {
        const result = resp.result;
        setSponsors((prev) =>
          refreshing || targetPage === 1
            ? ((result.sponsors ?? []) as BusinessSponsor[])
            : [...prev, ...((result.sponsors ?? []) as BusinessSponsor[])],
        );
        setSponsorTotal(result.total ?? 0);
        setSponsorPage(targetPage);
      }
      setSponsorsLoading(false);
      setSponsorsRefreshing(false);
    },
    [],
  );

  const fetchCompletions = useCallback(
    async (targetPage: number, refreshing = false) => {
      if (refreshing) setCompletionsRefreshing(true);
      const resp = await getSponsorOwnerCompletionsAPI(targetPage);
      if (resp?.status === 0) {
        const list = Array.isArray(resp.completions)
          ? (resp.completions as SponsorCompletionGroup[])
          : [];
        setGroups((prev) =>
          refreshing || targetPage === 1 ? list : [...prev, ...list],
        );
        // The backend paginates raw completions (10 per page) and only then
        // groups them, so "is there another page?" comes from the page's own
        // completion count rather than the group count.
        const returned = list.reduce(
          (sum, group) => sum + (group.sponsor_task_completions?.length ?? 0),
          0,
        );
        setHasMoreCompletions(returned > 0 && returned % COMPLETION_PAGE_SIZE === 0);
        setCompletionPage(targetPage);
      }
      setCompletionsLoading(false);
      setCompletionsRefreshing(false);
    },
    [],
  );

  useEffect(() => {
    fetchSponsors(1);
  }, [fetchSponsors]);

  useEffect(() => {
    fetchCompletions(1);
  }, [fetchCompletions]);

  // Review actions removed - business is read-only for sponsor tasks

  return {
    sponsors,
    sponsorTotal,
    sponsorPage,
    sponsorsLoading,
    sponsorsRefreshing,
    hasMoreSponsors: sponsors.length < sponsorTotal,
    loadMoreSponsors: () => fetchSponsors(sponsorPage + 1),
    refreshSponsors: () => fetchSponsors(1, true),

    groups,
    completionPage,
    completionsLoading,
    completionsRefreshing,
    hasMoreCompletions,
    loadMoreCompletions: () => fetchCompletions(completionPage + 1),
    refreshCompletions: () => fetchCompletions(1, true),

  };
}
